import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { Plus, ShieldCheck, CalendarPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dateFormat";

const CATS = ["general", "manager", "compensation", "harassment", "tooling", "other"];

function statusTone(s) {
  if (s === "open") return "orange";
  if (s === "resolved") return "green";
  return "slate";
}

export default function Grievances() {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("grievances_view");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general" });
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetTarget, setMeetTarget] = useState(null);
  const [meetForm, setMeetForm] = useState({ starts_at: "", duration_minutes: 30, location: "", notes: "" });

  const load = useCallback(async () => {
    if (!isAdmin) {
      try {
        const { data } = await api.get("/grievances/mine");
        setRows(data || []);
        setSummary(null);
      } catch (err) {
        setRows([]); setSummary(null);
      }
      return;
    }
    try {
      const [r, s] = await Promise.all([api.get("/grievances"), api.get("/grievances/summary")]);
      setRows(r.data); setSummary(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/grievances", form);
      toast.success("Submitted anonymously. Founders will see it; your identity is not stored.");
      setOpen(false);
      setForm({ title: "", body: "", category: "general" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/grievances/${id}`, { status }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearAllAdmin = async () => {
    if (!window.confirm(`Delete ALL ${rows.length} grievance entries and their meetings? This is irreversible.`)) return;
    try {
      const { data } = await api.post("/admin/clear-module", { module: "grievances", confirm: "CLEAR" });
      const wiped = Object.values(data.counts || {}).reduce((a, n) => a + n, 0);
      toast.success(`Cleared ${wiped} entries`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="grievances-page">
      <PageHeader
        eyebrow="Confidential"
        title="Grievances"
        subtitle={isAdmin ? "Anonymous issues raised by the team. Only authorized team leads can read these." : "Anonymously raise concerns. The portal does not log your identity with the submission."}
        actions={
          <div className="flex items-center gap-2">
            {isAdmin && rows.length > 0 && (
              <Button variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" onClick={clearAllAdmin} data-testid="clear-grievances-btn">
                Clear all
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-slate-700 text-white" data-testid="raise-grievance-btn"><ShieldCheck size={14} className="mr-1.5" />Raise anonymously</Button></DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Anonymous grievance</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500">Only the founders will see this. Your identity is never attached to the submission.</p>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="label-overline">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="rounded-sm" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="grievance-title" />
                <Textarea className="rounded-sm" rows={5} placeholder="Tell us what happened. Be as specific as you feel safe being." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="grievance-body" />
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="submit-grievance">Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {!isAdmin && (
        <>
          <div className="card-flat p-5 mb-4 flex items-start gap-3" data-testid="grievance-privacy-banner">
            <ShieldCheck size={20} className="text-[#F54800] shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <div className="font-display font-bold text-slate-900">Your grievances stay anonymous to others</div>
              <p className="text-xs text-slate-500 mt-1">
                Only the founders see what was raised. You can track your own submissions and their status below — this list is private to you.
              </p>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="card-flat p-8 text-center text-sm text-slate-500" data-testid="my-grievances-empty">
              You haven&apos;t raised any grievances yet. Use the button above when something needs attention.
            </div>
          ) : (
            <div className="space-y-3" data-testid="my-grievances-list">
              {rows.map((g) => (
                <div key={g.id} className="card-flat p-5" data-testid={`my-grievance-${g.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag tone={statusTone(g.status)} data-testid={`my-grievance-status-${g.id}`}>
                          {g.status === "in_progress" ? "working" : g.status}
                        </Tag>
                        <Tag tone="slate">{g.category}</Tag>
                        <span className="text-[11px] text-slate-400">{formatDate(g.created_at)}</span>
                      </div>
                      <div className="font-display text-base font-bold text-slate-900">{g.title}</div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{g.body}</p>
                      {g.meeting && (
                        <div className="mt-2 text-[11px] text-slate-500 border-l-2 border-[#F54800] pl-2">
                          A meeting was scheduled by the founders for {formatDateTime(g.meeting.starts_at)}.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isAdmin && (
        <div className="space-y-3">
          {rows.map((g) => (
            <div key={g.id} className="card-flat p-5" data-testid={`grievance-${g.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag tone={statusTone(g.status)}>{g.status}</Tag>
                    <Tag tone="slate">{g.category}</Tag>
                    <span className="text-xs text-slate-400 font-mono">#{g.anon_hash}</span>
                  </div>
                  <div className="font-display text-lg font-bold text-slate-900">{g.title}</div>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{g.body}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {g.status !== "in_progress" && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setStatus(g.id, "in_progress")}>Acknowledge</Button>}
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setMeetTarget(g); setMeetForm({ starts_at: "", duration_minutes: 30, location: "", notes: g.title }); setMeetOpen(true); }} data-testid={`meet-${g.id}`}><CalendarPlus size={12} className="mr-1" />Schedule meeting</Button>
                  {g.meeting && (
                    <div className="text-[10px] text-slate-500 border-l-2 border-[#F54800] pl-2">
                      <div>{formatDateTime(g.meeting.starts_at)}</div>
                      {g.meeting.meet_link && <a href={g.meeting.meet_link} target="_blank" rel="noreferrer" className="text-[#F54800] hover:underline inline-flex items-center gap-1">Meet <ExternalLink size={9} /></a>}
                      <a href={`/api/grievances/${g.id}/meeting.ics`} className="text-[#F54800] hover:underline ml-2">.ics</a>
                    </div>
                  )}
                  {g.status !== "resolved" && <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setStatus(g.id, "resolved")}>Resolve</Button>}
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="card-flat p-8 text-center text-sm text-slate-500">No grievances filed.</div>}
        </div>
      )}

      <Dialog open={meetOpen} onOpenChange={setMeetOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display">Schedule meeting · {meetTarget?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-slate-500">Pick a time and a location. Leave location blank to auto-attach a Google Meet link.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Start</Label><Input type="datetime-local" className="rounded-sm mt-1" value={meetForm.starts_at} onChange={(e) => setMeetForm({ ...meetForm, starts_at: e.target.value })} data-testid="meet-start" /></div>
              <div><Label className="label-overline">Duration (min)</Label><Input type="number" min={15} step={15} className="rounded-sm mt-1" value={meetForm.duration_minutes} onChange={(e) => setMeetForm({ ...meetForm, duration_minutes: Number(e.target.value) || 30 })} /></div>
            </div>
            <div><Label className="label-overline">Location</Label><Input className="rounded-sm mt-1" placeholder="e.g. Studio, room 3 (blank = Google Meet)" value={meetForm.location} onChange={(e) => setMeetForm({ ...meetForm, location: e.target.value })} data-testid="meet-location" /></div>
            <div><Label className="label-overline">Notes</Label><Textarea rows={2} className="rounded-sm mt-1" value={meetForm.notes} onChange={(e) => setMeetForm({ ...meetForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={async () => {
              if (!meetForm.starts_at) { toast.error("Pick a start time"); return; }
              try {
                const startsIso = new Date(meetForm.starts_at).toISOString();
                const { data } = await api.post(`/grievances/${meetTarget.id}/meeting`, {
                  starts_at: startsIso,
                  duration_minutes: Number(meetForm.duration_minutes) || 30,
                  location: meetForm.location,
                  notes: meetForm.notes,
                });
                // Trigger .ics download
                const a = document.createElement("a");
                a.href = data.ics_path;
                a.download = `grievance-${meetTarget.id}.ics`;
                a.click();
                if (data.meeting?.meet_link) {
                  window.open(data.meeting.meet_link, "_blank", "noopener");
                }
                toast.success("Meeting scheduled — invite downloaded");
                setMeetOpen(false);
                load();
              } catch (e) { toast.error(formatApiError(e)); }
            }} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="meet-submit">Schedule + download .ics</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
